import { canReachBackendApi, resolveApiRequestUrl } from './apiRequestUrl';

/**
 * @returns {{ message: string, code?: string }}
 */
async function friendlyErrorFromResponse(res) {
  let body = null;
  const ct = res.headers.get('Content-Type') || '';
  if (ct.includes('application/json')) {
    try {
      body = await res.json();
    } catch {
      /* ignore parse errors */
    }
  }
  const serverMsg = typeof body?.error === 'string' ? body.error : null;
  switch (res.status) {
    case 401:
      return { message: 'Your session could not be verified. Please sign out and sign in again.' };
    case 400:
      return { message: serverMsg || 'The request was invalid. Please try again.' };
    case 404:
      return { message: serverMsg || 'The generation job was not found.' };
    case 502:
      return { message: serverMsg || 'Video generation failed. Please try again.' };
    case 503:
      return { message: serverMsg || 'The backend Gemini API key is missing or invalid.' };
    default:
      return { message: serverMsg || 'Something went wrong. Please try again.' };
  }
}

/**
 * @param {import('firebase/auth').User} user
 * @param {{
 *   firstFrameBlob: Blob;
 *   firstFrameName: string;
 *   lastFrameBlob: Blob;
 *   lastFrameName: string;
 *   prompt: string;
 *   durationSeconds: string;
 *   resolution: string;
 *   model: string;
 *   signal?: AbortSignal;
 * }} params
 */
export async function startVeoFloorplanVideoJob(user, params) {
  if (!user) {
    return { ok: false, error: 'You must be signed in to generate a video.' };
  }
  if (!canReachBackendApi()) {
    return {
      ok: false,
      error: 'The app is not configured to reach the design server. Please contact support.',
    };
  }

  try {
    const idToken = await user.getIdToken(true);
    const formData = new FormData();
    formData.append('firstFrame', params.firstFrameBlob, params.firstFrameName || 'first-frame.jpg');
    formData.append('lastFrame', params.lastFrameBlob, params.lastFrameName || 'last-frame.jpg');
    formData.append('prompt', params.prompt);
    formData.append('durationSeconds', params.durationSeconds);
    formData.append('resolution', params.resolution);
    formData.append('model', params.model);

    const res = await fetch(resolveApiRequestUrl('/api/test/veo-floorplan-video'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      body: formData,
      signal: params.signal,
    });

    if (res.ok) {
      let body = null;
      try {
        body = await res.json();
      } catch {
        /* ignore */
      }
      const jobId = typeof body?.jobId === 'string' ? body.jobId : '';
      if (!jobId) {
        return { ok: false, error: 'The server did not return a job ID.' };
      }
      return {
        ok: true,
        jobId,
        status: typeof body?.status === 'string' ? body.status : 'processing',
      };
    }

    const { message, code } = await friendlyErrorFromResponse(res);
    return { ok: false, error: message, code };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return { ok: false, error: 'The upload request was cancelled.', code: 'aborted' };
    }
    const message = err instanceof Error ? err.message : String(err ?? '');
    const likelyCors =
      typeof message === 'string' &&
      (message.includes('Failed to fetch') || message.includes('NetworkError'));
    return {
      ok: false,
      error: likelyCors
        ? 'Could not reach the server. Check your connection and try again.'
        : 'Could not reach the server. Check your connection and try again.',
    };
  }
}

/**
 * @param {import('firebase/auth').User} user
 * @param {string} jobId
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function fetchVeoFloorplanVideoJobStatus(user, jobId, options = {}) {
  if (!user) {
    return { ok: false, error: 'You must be signed in.' };
  }
  if (!canReachBackendApi()) {
    return { ok: false, error: 'The app is not configured to reach the design server.' };
  }

  try {
    const idToken = await user.getIdToken(false);
    const res = await fetch(resolveApiRequestUrl(`/api/test/veo-floorplan-video/${jobId}`), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      signal: options.signal,
    });

    if (res.ok) {
      let body = null;
      try {
        body = await res.json();
      } catch {
        /* ignore */
      }
      return {
        ok: true,
        jobId: typeof body?.jobId === 'string' ? body.jobId : jobId,
        status: typeof body?.status === 'string' ? body.status : 'processing',
        message: typeof body?.message === 'string' ? body.message : '',
        videoUrl: typeof body?.videoUrl === 'string' ? body.videoUrl : null,
        error: typeof body?.error === 'string' ? body.error : null,
        model: typeof body?.model === 'string' ? body.model : null,
        durationSeconds:
          typeof body?.durationSeconds === 'string' ? body.durationSeconds : null,
        resolution: typeof body?.resolution === 'string' ? body.resolution : null,
        safetyInfo: body?.safetyInfo ?? null,
      };
    }

    const { message, code } = await friendlyErrorFromResponse(res);
    return { ok: false, error: message, code };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return { ok: false, error: 'Status polling was cancelled.', code: 'aborted' };
    }
    return { ok: false, error: 'Could not reach the server. Check your connection and try again.' };
  }
}

/**
 * @param {import('firebase/auth').User} user
 * @param {string} jobId
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function cancelVeoFloorplanVideoJob(user, jobId, options = {}) {
  if (!user) {
    return { ok: false, error: 'You must be signed in.' };
  }

  try {
    const idToken = await user.getIdToken(false);
    const res = await fetch(resolveApiRequestUrl(`/api/test/veo-floorplan-video/${jobId}`), {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      signal: options.signal,
    });

    if (res.ok) {
      let body = null;
      try {
        body = await res.json();
      } catch {
        /* ignore */
      }
      return {
        ok: true,
        status: typeof body?.status === 'string' ? body.status : 'failed',
        message: typeof body?.message === 'string' ? body.message : 'Cancelled',
        error: typeof body?.error === 'string' ? body.error : null,
      };
    }

    const { message, code } = await friendlyErrorFromResponse(res);
    return { ok: false, error: message, code };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return { ok: false, error: 'Cancel request was aborted.', code: 'aborted' };
    }
    return { ok: false, error: 'Could not cancel the job. Please try again.' };
  }
}
